<script setup>
import { Head, Link, usePage } from '@inertiajs/vue3';
import { computed } from 'vue';
import FrontLayout from '@/Layouts/FrontLayout.vue';
import PageHero from '@/Components/Front/PageHero.vue';
import SocialShare from '@/Components/Front/SocialShare.vue';

const props = defineProps({
    kecamatan: Object,
    artikel: Object,
    relatedArtikel: Array,
    otherArtikel: Array,
    setting: Object,
    kecamatanFooter: Array,
    processedContent: String,
    // SEO props
    title: String,
    description: String,
    keywords: String,
    canonical: String,
    image: String,
    og_title: String,
    og_description: String,
    og_image: String,
    og_url: String,
    og_type: String,
    twitter_title: String,
    twitter_description: String,
    twitter_image: String,
    type: String,
    published_time: String,
    modified_time: String,
    author: String,
    canonical_url: String,
});

const page = usePage();

// Format date helper
const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Intl.DateTimeFormat('id-ID', { day: 'numeric', month: 'short', year: 'numeric' }).format(new Date(dateString));
};

// WhatsApp Link Logic
const waNumber = computed(() => props.setting?.setting_no_wa || '6281234567890');
const waLink = computed(() => {
    const text = encodeURIComponent(`Halo, saya membaca artikel "${props.artikel.judul}" dan tertarik dengan layanan rental mobil di ${props.kecamatan.nama_kecamatan}`);
    return `https://api.whatsapp.com/send?phone=${waNumber.value}&text=${text}`;
});

const socialLinks = computed(() => ({
    facebook: props.setting?.setting_url_facebook,
    instagram: props.setting?.setting_url_instagram,
    tiktok: props.setting?.setting_url_tiktok
}));

// JSON-LD Schema
const jsonLd = computed(() => {
    const appUrl = page.props.app_url || window.location.origin;
    const currentUrl = props.canonical_url || window.location.href;
    const logoUrl = props.setting?.setting_logo ? `/storage/logo/${props.setting.setting_logo}` : '/front/img/logo.png';
    const imageUrl = props.artikel.featured_image ? `${appUrl}/storage/${props.artikel.featured_image}` : `${appUrl}/front/img/default-news.jpg`;
    
    return {
        "@context": "https://schema.org",
        "@graph": [
            {
                "@type": "Article",
                "headline": props.artikel.judul,
                "description": props.description,
                "image": imageUrl,
                "datePublished": props.artikel.published_at || props.artikel.created_at,
                "dateModified": props.artikel.updated_at,
                "author": {
                    "@type": "Organization", // Usually organization for area articles, or admin
                    "name": props.setting?.setting_title || 'Gotransport',
                    "url": appUrl
                },
                "publisher": {
                    "@type": "Organization",
                    "name": props.setting?.setting_title || 'Gotransport Banyuwangi',
                    "url": appUrl,
                    "logo": {
                        "@type": "ImageObject",
                        "url": `${appUrl}${logoUrl}`
                    }
                },
                "mainEntityOfPage": {
                    "@type": "WebPage",
                    "@id": currentUrl
                }
            },
            {
                "@type": "BreadcrumbList",
                "itemListElement": [
                    { 
                        "@type": "ListItem", 
                        "position": 1, 
                        "name": "Beranda", 
                        "item": appUrl 
                    },
                    { 
                        "@type": "ListItem", 
                        "position": 2, 
                        "name": "Area Layanan", 
                        "item": `${appUrl}/area-layanan` 
                    },
                    { 
                        "@type": "ListItem", 
                        "position": 3, 
                        "name": props.kecamatan.nama_kecamatan, 
                        "item": `${appUrl}/area-layanan/${props.kecamatan.slug}` 
                    },
                    { 
                        "@type": "ListItem", 
                        "position": 4, 
                        "name": props.artikel.judul, 
                        "item": currentUrl 
                    }
                ]
            }
        ]
    };
});
</script>

<template>
    <FrontLayout 
        :setting="setting"
        :title="title"
        :metaDescription="description"
        :metaKeywords="keywords"
        :ogTitle="og_title"
        :ogDescription="og_description"
        :ogImage="og_image"
        :ogUrl="og_url"
    >
        <Head>
            <link v-if="canonical_url" rel="canonical" :href="canonical_url" />
            <meta property="og:type" content="article" />
            <meta property="og:site_name" content="Gotransport Banyuwangi" />
            <meta name="author" :content="author || 'Gotransport'" />
            <meta v-if="published_time" name="article:published_time" :content="published_time" />
            <meta v-if="modified_time" name="article:modified_time" :content="modified_time" />
            <component :is="'script'" type="application/ld+json">{{ JSON.stringify(jsonLd) }}</component>
        </Head>

        <!-- Hero Section -->
        <PageHero 
            :title="artikel.judul"
            :subtitle="`Artikel rental mobil untuk kecamatan ${kecamatan.nama_kecamatan}, Banyuwangi`"
            :breadcrumbs="[
                { label: 'Beranda', url: '/' },
                { label: 'Area Layanan', url: '/area-layanan' }, // Adjust if needed
                { label: kecamatan.nama_kecamatan, url: `/area-layanan/${kecamatan.slug}` }
            ]"
        >
             <div class="mt-4 max-w-3xl mx-auto">
                <p class="text-lg text-white/90 line-clamp-2">
                    {{ description }}
                </p>
            </div>
        </PageHero>

        <!-- Article Content Section -->
        <section class="py-8 md:py-10 bg-white dark:bg-gray-900 border-t border-gray-100 dark:border-gray-800">
            <div class="container mx-auto px-4 lg:px-6 xl:px-8 max-w-7xl">
                <div class="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    <!-- Main Content -->
                    <div class="lg:col-span-2">
                        <article class="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
                            <!-- Article Image (Full Width) -->
                            <div v-if="artikel.featured_image" class="w-full">
                                <img :src="`/storage/${artikel.featured_image}`"
                                    :alt="artikel.judul" 
                                    class="w-full h-auto object-cover"
                                    loading="lazy">
                            </div>

                            <div class="p-4">
                                <!-- Article Meta -->
                                <div class="flex flex-wrap gap-2 mb-6 pb-4 border-b border-gray-200 dark:border-gray-700">
                                    <span class="inline-flex items-center gap-1 px-3 py-1 bg-orange-50 dark:bg-gray-700 text-orange-700 dark:text-orange-300 text-xs font-medium border border-orange-200 dark:border-gray-600 rounded-full">
                                        <i class="fa fa-map-marker"></i>
                                        {{ kecamatan.nama_kecamatan }}
                                    </span>
                                    <span class="inline-flex items-center gap-1 px-3 py-1 bg-blue-50 dark:bg-gray-700 text-blue-700 dark:text-blue-300 text-xs font-medium border border-blue-200 dark:border-gray-600 rounded-full">
                                        <i class="fa fa-calendar"></i>
                                        {{ formatDate(artikel.published_at || artikel.created_at) }}
                                    </span>
                                    <span v-if="artikel.is_featured" class="inline-flex items-center gap-1 px-3 py-1 bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-200 text-xs font-medium border border-yellow-300 dark:border-yellow-700 rounded-full">
                                        <i class="fa fa-star"></i>
                                        Unggulan
                                    </span>
                                    <span v-if="artikel.views > 0" class="inline-flex items-center gap-1 px-3 py-1 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 text-xs font-medium border border-gray-200 dark:border-gray-600 rounded-full">
                                        <i class="fa fa-eye"></i>
                                        {{ artikel.views }} views
                                    </span>
                                </div>

                                <!-- Article Main Heading -->
                                <h1 class="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white mb-4">
                                    {{ artikel.judul }}
                                </h1>

                                <!-- Article Content -->
                                <div class="prose prose-sm dark:prose-invert max-w-none text-gray-700 dark:text-gray-300 leading-relaxed mb-6 prose-img:rounded-xl prose-a:text-blue-600 hover:prose-a:text-blue-700 prose-headings:text-gray-900 dark:prose-headings:text-white prose-strong:text-gray-900 dark:prose-strong:text-white" v-html="processedContent"></div>

                                <!-- Social Share -->
                                <div class="pt-4 border-t border-gray-200 dark:border-gray-700">
                                    <SocialShare 
                                        :url="canonical_url"
                                        :title="artikel.judul"
                                        layout="card"
                                    />
                                </div>

                                <!-- Back to Kecamatan -->
                                <div class="mt-6 p-4 bg-orange-50 dark:bg-gray-700/50 border border-orange-200 dark:border-gray-600 rounded-xl">
                                    <div class="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                                        <div>
                                            <h3 class="text-lg font-bold text-gray-900 dark:text-white mb-1 flex items-center gap-2">
                                                <i class="fa fa-map-marker text-orange-600 dark:text-orange-400"></i>
                                                {{ kecamatan.nama_kecamatan }}
                                            </h3>
                                            <p class="text-sm text-gray-600 dark:text-gray-400">
                                                {{ otherArtikel.length > 0 ? `Terdapat ${otherArtikel.length} artikel lainnya` : 'Belum ada artikel lain dari kecamatan ini' }}
                                            </p>
                                        </div>
                                        <Link :href="`/area-layanan/${kecamatan.slug}`" 
                                           class="px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white text-sm font-bold transition-colors whitespace-nowrap rounded-lg flex items-center">
                                            <i class="fa fa-arrow-left mr-1"></i>
                                            <span>{{ otherArtikel.length > 0 ? 'Lihat Lainnya' : 'Kembali' }}</span>
                                        </Link>
                                    </div>
                                </div>
                            </div>
                        </article>
                    </div>

                    <!-- Sidebar -->
                    <div class="lg:col-span-1">
                        <aside class="space-y-6">
                            <!-- Armada CTA Widget -->
                            <div class="bg-gradient-to-br from-blue-50 to-blue-100 dark:from-gray-700 dark:to-gray-800 border border-blue-300 dark:border-gray-600 p-4 rounded-xl">
                                <div class="text-center mb-3">
                                    <i class="fa fa-car text-3xl text-blue-600 dark:text-blue-400 mb-2"></i>
                                    <h3 class="text-lg font-bold text-gray-900 dark:text-white">Butuh Rental Mobil?</h3>
                                </div>
                                <p class="text-sm text-gray-700 dark:text-gray-300 mb-4 text-center">
                                    Dapatkan penawaran terbaik rental mobil untuk {{ kecamatan.nama_kecamatan }}
                                </p>
                                
                                <div class="space-y-2">
                                    <Link href="/daftar-armada" 
                                       class="block w-full text-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-bold transition-colors rounded-lg flex items-center justify-center gap-2">
                                        <i class="fa fa-eye"></i> Lihat Armada
                                    </Link>
                                    
                                    <a :href="waLink" 
                                       class="block w-full text-center px-4 py-2 bg-green-700 hover:bg-green-800 text-white font-bold transition-colors rounded-lg flex items-center justify-center gap-2" 
                                       target="_blank" rel="nofollow noopener">
                                        <i class="fa fa-whatsapp"></i> WhatsApp
                                    </a>
                                </div>

                                <!-- Social Media -->
                                <div v-if="socialLinks.facebook || socialLinks.instagram || socialLinks.tiktok" class="flex justify-center gap-2 mt-4 pt-4 border-t border-blue-200 dark:border-gray-600">
                                    <a v-if="socialLinks.facebook" :href="socialLinks.facebook" target="_blank" rel="nofollow noopener"
                                       class="w-8 h-8 flex items-center justify-center bg-blue-600 hover:bg-blue-700 text-white transition-colors rounded-full">
                                        <i class="fa fa-facebook"></i>
                                    </a>
                                    <a v-if="socialLinks.instagram" :href="socialLinks.instagram" target="_blank" rel="nofollow noopener"
                                       class="w-8 h-8 flex items-center justify-center bg-pink-600 hover:bg-pink-700 text-white transition-colors rounded-full">
                                        <i class="fa fa-instagram"></i>
                                    </a>
                                    <a v-if="socialLinks.tiktok" :href="socialLinks.tiktok" target="_blank" rel="nofollow noopener"
                                       class="w-8 h-8 flex items-center justify-center bg-gray-900 hover:bg-black text-white transition-colors rounded-full">
                                        <i class="fa fa-music"></i>
                                    </a>
                                </div>
                            </div>

                            <!-- Other Articles from Same Kecamatan Widget -->
                            <div v-if="otherArtikel.length > 0" class="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 p-4 rounded-xl">
                                <h3 class="text-lg font-bold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                                    <i class="fa fa-newspaper-o text-blue-600 dark:text-blue-400"></i>
                                    Artikel Lain dari {{ kecamatan.nama_kecamatan }}
                                </h3>
                                <p class="text-xs text-gray-600 dark:text-gray-400 mb-3">{{ otherArtikel.length }} artikel tersedia</p>
                                <div class="space-y-3">
                                    <article v-for="other in otherArtikel" :key="other.id" class="pb-3 border-b border-gray-100 dark:border-gray-700 last:border-b-0">
                                        <Link v-if="other.featured_image" :href="`/area-layanan/${kecamatan.slug}/${other.slug}`" class="block mb-2">
                                            <div class="bg-gray-100 dark:bg-gray-700 overflow-hidden rounded-lg">
                                                <img :src="`/storage/${other.featured_image}`"
                                                    :alt="other.judul" 
                                                    class="w-full h-auto hover:scale-105 transition-transform"
                                                    loading="lazy">
                                            </div>
                                        </Link>
                                        
                                        <h4 class="text-sm font-bold text-gray-900 dark:text-white mb-1 line-clamp-2 hover:text-blue-600 dark:hover:text-blue-400 transition-colors">
                                            <Link :href="`/area-layanan/${kecamatan.slug}/${other.slug}`">
                                                {{ other.judul }}
                                            </Link>
                                        </h4>
                                        
                                        <div class="flex flex-wrap gap-1 mb-2">
                                            <span class="inline-flex items-center gap-1 px-1.5 py-0.5 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 text-xs rounded">
                                                <i class="fa fa-calendar"></i>
                                                {{ formatDate(other.published_at || other.created_at) }}
                                            </span>
                                            <span v-if="other.is_featured" class="inline-flex items-center gap-1 px-1.5 py-0.5 bg-yellow-100 text-yellow-800 text-xs rounded">
                                                <i class="fa fa-star"></i>
                                            </span>
                                        </div>
                                        
                                        <Link :href="`/area-layanan/${kecamatan.slug}/${other.slug}`" 
                                           class="inline-flex items-center text-xs text-blue-600 hover:text-blue-700 font-medium">
                                            Baca Selengkapnya
                                            <i class="fa fa-arrow-right ml-1"></i>
                                        </Link>
                                    </article>
                                </div>
                            </div>

                            <!-- Other Kecamatan Widget - Show All -->
                            <div v-if="kecamatanFooter && kecamatanFooter.length > 0" class="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 p-4 rounded-xl">
                                <h3 class="text-lg font-bold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                                    <i class="fa fa-map text-gray-600 dark:text-gray-400"></i>
                                    Area Layanan Lainnya
                                </h3>
                                <p class="text-xs text-gray-600 dark:text-gray-400 mb-3">{{ kecamatanFooter.length }} kecamatan lainnya</p>
                                <div class="space-y-2 max-h-96 overflow-y-auto pr-2 custom-scrollbar">
                                    <Link v-for="otherKec in kecamatanFooter" :key="otherKec.id" 
                                       :href="`/area-layanan/${otherKec.slug}`"
                                       class="flex items-center gap-2 py-2 border-b border-gray-100 dark:border-gray-700 last:border-b-0 text-sm text-gray-700 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400 transition-colors">
                                        <i class="fa fa-map-marker text-orange-600 dark:text-orange-400"></i>
                                        {{ otherKec.nama_kecamatan }}
                                    </Link>
                                </div>
                            </div>
                        </aside>
                    </div>
                </div>
            </div>
        </section>

        <!-- Map Section - Full Width -->
        <section v-if="setting.setting_map" class="py-8 md:py-10 bg-white dark:bg-gray-900 border-t border-gray-200 dark:border-gray-800">
            <div class="container mx-auto px-4 lg:px-6 xl:px-8 max-w-7xl mb-6">
                <div class="text-center">
                    <h3 class="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white mb-2 flex items-center justify-center gap-2">
                        <i class="fa fa-map-marker text-orange-600 dark:text-orange-400"></i>
                        Lokasi Kantor Kami
                    </h3>
                    <p class="text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
                        Kunjungi kantor kami untuk konsultasi rental mobil di {{ kecamatan.nama_kecamatan }}.
                        Tim berpengalaman siap membantu kebutuhan transportasi Anda.
                    </p>
                </div>
            </div>

            <!-- Full Width Map -->
            <div class="w-full">
                <div class="relative w-full overflow-hidden h-[450px]">
                     <div class="w-full h-full [&_iframe]:w-full [&_iframe]:h-full [&_iframe]:border-0" v-html="setting.setting_map"></div>
                </div>
            </div>
        </section>
    </FrontLayout>
</template>
